import { useTranslation } from 'react-i18next';
import { Edit2, Lock, Unlock, Trash2, X } from 'lucide-react';
import { Availability } from '../types/availability';
import { formatDate } from '../utils/formatDate';
import { calculateNights } from '../utils/calculateNights';
import { categorizeAvailabilityReason } from '../utils/availabilitySourceBadges';

interface AvailabilityPopoverProps {
  readonly availability: Availability;
  readonly sourceBadgeLabel?: string;
  readonly onClose: () => void;
  readonly onEdit: (availability: Availability) => void;
  readonly onBlock: (id: string) => void;
  readonly onUnblock: (id: string) => void;
  readonly onRemove: (id: string) => void;
  readonly isMutating: boolean;
  readonly openAbove: boolean;
}

const statusBadgeClass = (status: Availability['status']): string => {
  switch (status) {
    case 'AVAILABLE':
      return 'bg-green-100 text-green-800 border border-green-200';
    case 'BLOCKED':
      return 'bg-red-100 text-red-800 border border-red-200';
    case 'BOOKED':
      return 'bg-gray-100 text-gray-800 border border-gray-200';
  }
};

export const AvailabilityPopover = ({
  availability,
  sourceBadgeLabel,
  onClose,
  onEdit,
  onBlock,
  onUnblock,
  onRemove,
  isMutating,
  openAbove,
}: AvailabilityPopoverProps) => {
  const { t } = useTranslation(['common', 'pages', 'forms', 'badges']);

  const nights = calculateNights(availability.startDate, availability.endDate);
  const positionClass = openAbove ? 'bottom-full mb-1' : 'top-full mt-1';
  const reasonCategory = categorizeAvailabilityReason(availability);
  const reasonText = (() => {
    if (!reasonCategory) return null;
    switch (reasonCategory.kind) {
      case 'externallyBooked':
        return t('pages:externallyBookedViaSource', { source: reasonCategory.sourceLabel });
      case 'externallyBlocked':
        return t('pages:externallyBlockedViaSource', { source: reasonCategory.sourceLabel });
      case 'manualBlocked':
        return t('pages:manualBlockedLabel');
      case 'platformBooked':
        return t('pages:platformBookedLabel');
    }
  })();

  return (
    <div
      role="dialog"
      aria-label={t('pages:availabilityDetailsAriaLabel') as string}
      className={`absolute z-50 left-0 w-64 bg-white border border-gray-200 rounded-xl shadow-lg ${positionClass}`}
    >
      {/* Header: status badge + close */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusBadgeClass(availability.status)}`}>
          {availability.status}
        </span>
        <button
          onClick={onClose}
          aria-label={t('pages:closePanel') as string}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X size={14} className="text-gray-500" />
        </button>
      </div>

      {/* Body: dates, nights, price, notes */}
      <div className="px-3 py-2 space-y-1 text-sm">
        <p className="text-gray-700">
          {formatDate(availability.startDate)} → {formatDate(availability.endDate)}
        </p>
        <p className="text-gray-500">
          {nights} {nights === 1 ? 'night' : 'nights'}
          {availability.pricePerNight > 0 && (
            <> · €{availability.pricePerNight}/night</>
          )}
        </p>
        {availability.notes && (
          <p className="text-gray-500 text-xs italic">{availability.notes}</p>
        )}
        {sourceBadgeLabel && (
          <p className="text-gray-500 text-xs">
            {t('pages:availabilitySourceBadge')}: {sourceBadgeLabel}
          </p>
        )}
        {reasonText && (
          <p className="text-gray-500 text-xs" data-testid="availability-reason">
            {reasonText}
          </p>
        )}
      </div>

      {/* Actions */}
      {availability.status !== 'BOOKED' && (
        <div className="flex items-center gap-1 px-3 py-2 border-t border-gray-100">
          <button
            onClick={() => onEdit(availability)}
            disabled={isMutating}
            title={t('pages:editAvailability') as string}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <Edit2 size={14} className="text-gray-600" />
          </button>

          {availability.status === 'AVAILABLE' ? (
            <button
              onClick={() => onBlock(availability.id)}
              disabled={isMutating}
              title={t('pages:blockDates') as string}
              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            >
              <Lock size={14} className="text-red-600" />
            </button>
          ) : (
            <button
              onClick={() => onUnblock(availability.id)}
              disabled={isMutating}
              title={t('pages:unblockDates') as string}
              className="p-1.5 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
            >
              <Unlock size={14} className="text-green-600" />
            </button>
          )}

          <button
            onClick={() => onRemove(availability.id)}
            disabled={isMutating}
            title={t('common:delete') as string}
            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          >
            <Trash2 size={14} className="text-red-600" />
          </button>
        </div>
      )}
    </div>
  );
};
